import { FiscalYear } from 'app/internal/shared/models/fiscal-year';
import { Injectable } from '@angular/core';
import { bindCallback, Observable, throwError } from 'rxjs';
import { ApiService, ApiHelperService } from '../../../../shared/services';
import { Property, PropertyType, PropertyFinancial, PropertyReserveStudy } from '../models';
import { Workspace, WorkspaceUserProperty } from '../../../shared/models';
import { PropertyReconicileRequest } from '../models/property-reconcile-request';
import { map, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { PropertySearchRequest, PropertySearchResult } from '../../../user/models';
import { PropertySearchFilter } from '../../../admin/shared/models';

@Injectable()
export class PropertyService {
  // todo refactor every group to single service
  propertyEndpointUrl: string;
  propertyTypesEndpointUrl: string;
  financialsEndpointUrl: string;
  selectedPropertyId = 'selected_property_id';
  selectedFiscalYear = 'selected_fiscal_year';
  propertyFilter = 'property_filter';

  constructor(
    private api: ApiService,
    { propertyServiceUrl }: ApiHelperService) {
    this.propertyEndpointUrl = `${propertyServiceUrl}/api/property`;
    this.propertyTypesEndpointUrl = `${propertyServiceUrl}/api/propertytype`;
    this.financialsEndpointUrl = `${propertyServiceUrl}/api/propertyfinancial`;
  }

  deleteProperty(propertyId: string): Observable<any> {
    // Clear the selected propertyId if that is the property being deleted
    const selectedPropertyId = this.getSelectedPropertyId();
    if (selectedPropertyId === propertyId) {
      window.localStorage.removeItem(this.selectedPropertyId);
    }

    return this.api.delete(`${this.propertyEndpointUrl}/${propertyId}`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  getProperties(request: PropertySearchRequest): Observable<PropertySearchResult> {
    return this.api.post(`${this.propertyEndpointUrl}/search`, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  setPropertyFilter(filter: PropertySearchFilter) {
    if (filter) {
      window.localStorage.setItem(this.propertyFilter.toString(), filter.filter);
    }
  }

  getAllProperties(): Observable<WorkspaceUserProperty[]> {
    return this.api.get(`${this.propertyEndpointUrl}/all`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  getPropertyById(propertyId: string): Observable<Property> {
    return this.api.get(`${this.propertyEndpointUrl}/${propertyId}`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  loadPropertyById(propertyId: string): Observable<Property> {
    return this.api.get(`${this.propertyEndpointUrl}/${propertyId}/load`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  savePropertyDetails(property: Property): Observable<Property> {
    return this.api.put(`${this.propertyEndpointUrl}/${property.propertyId}`, property).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  addProperty(property: Property): Observable<Property> {

    const prop = {
      name: property.name,
      workspaceId: property.workspaceId,
      isTest: property.isTest,
      addressLine1: property.addressLine1,
      addressLine2: property.addressLine2,
      city: property.city,
      state: property.state,
      zipCode: property.zipCode,
      yearBuilt: property.yearBuilt,
      propertyTypeId: property.propertyTypeId,
      isCondoConverted: property.isCondoConverted,
      numberOfUnits: property.numberOfUnits,
      numberOfFloors: property.numberOfFloors,
      yearOfConversion: property.yearOfConversion == null ? 0 : property.yearOfConversion,
      propertyDescription: property.propertyDescription,
      propertyFinancial: property.propertyFinancial,
      reserveStudy: property.reserveStudy
    };

    return this.api.post(`${this.propertyEndpointUrl}`, prop).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  convertAddressToLatLong(property: Property): Observable<google.maps.LatLngLiteral> {
    const geocoder = new google.maps.Geocoder();
    const address = `${property.addressLine1} ${property.addressLine2} ${property.city}, ${property.state} ${property.zipCode}`;

    const bound = bindCallback((
      fullAddress: string,
      callback: (results: google.maps.GeocoderResult[], status: string) => void
    ) => geocoder.geocode({ 'address': fullAddress }, callback));

    return bound(address).pipe(
      map(response => response[0][0].geometry.location.toJSON()),
      catchError((error) => this.handleError(error))
    );
  }

  getPropertyTypes(): Observable<PropertyType[]> {
    return this.api.get(`${this.propertyTypesEndpointUrl}`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  getNewPropertyWorkspaces(): Observable<Workspace[]> {
    return this.api.get(`${this.propertyEndpointUrl}/newpropertyworkspaces`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  getPropertyFinancialEntries(propertyId: string, fiscalYear: number) {
    return this.api.get(`${this.financialsEndpointUrl}/${propertyId}/${fiscalYear}`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  savePropertyFinancialEntries(financialEntry: PropertyFinancial, fiscalYear: number, isPlanningAssumptions: boolean) {
    return this.api.put(`${this.financialsEndpointUrl}/${fiscalYear}/${isPlanningAssumptions}`, financialEntry).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  removePropertyProfileImage(propertyId: string): Observable<any> {
    return this.api.delete(`${this.propertyEndpointUrl}/${propertyId}/profileimage`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  setSelectedPropertyId(propertyId: string) {
    window.localStorage.setItem(this.selectedPropertyId, propertyId);
  }

  getSelectedPropertyId(): string {
    return window.localStorage.getItem(this.selectedPropertyId);
  }

  reconcileFiscalYear(propertyId: string, reconcileRequest: PropertyReconicileRequest) {
    return this.api.post(`${this.propertyEndpointUrl}/${propertyId}/reconcilefiscalyear`, reconcileRequest);
  }

  setSelectedFiscalYear(fiscalYear: FiscalYear) {
    window.localStorage.setItem(this.selectedFiscalYear, fiscalYear.year.toString());
  }

  getDefaultFiscalYear(property: Property): FiscalYear {
    // look at the last selected value and see if we should reload that year.
    let fiscalYearObject = null;
    const fiscalYear = parseInt(window.localStorage.getItem(this.selectedFiscalYear));

    if (fiscalYear != null && !isNaN(fiscalYear)) {
      fiscalYearObject = property.fiscalYears.find(x => x.year === fiscalYear);
    }

    // if the fiscalYear doesn't exist for this property or it requires setup, default to current year.
    if (fiscalYearObject == null || fiscalYearObject.requireSetup) {
      fiscalYearObject = property.fiscalYears.find(x => x.isCurrentFiscalYear === true);

      // if the current year hasn't been setup, go back another year.
      if (fiscalYearObject.requireSetup) {
        fiscalYearObject = property.fiscalYears.find(x => x.year === fiscalYearObject.year - 1);
      }
    }

    return fiscalYearObject;
  }

  getAllPropertyReserveStudies(propertyId: string): Observable<PropertyReserveStudy[]> {

    let selectedPropertyId = this.getSelectedPropertyId();
    if (propertyId) {
      selectedPropertyId = propertyId;
    }

    return this.api.get(`${this.propertyEndpointUrl}/${selectedPropertyId}/reservestudy`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  updatePropertyReserveStudy(reserveStudy: PropertyReserveStudy): Observable<PropertyReserveStudy> {
    return this.api.put(`${this.propertyEndpointUrl}/${reserveStudy.propertyId}/reservestudy/${reserveStudy.propertyReserveStudyId}`, reserveStudy).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  addPropertyReserveStudy(reserveStudy: PropertyReserveStudy): Observable<PropertyReserveStudy> {
    return this.api.post(`${this.propertyEndpointUrl}/${reserveStudy.propertyId}/reservestudy`, reserveStudy).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  deletePropertyReserveStudy(propertyId: string, propertyReserveStudyId: string): Observable<any> {
    return this.api.delete(`${this.propertyEndpointUrl}/${propertyId}/reservestudy/${propertyReserveStudyId}`).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  private handleError(response: HttpErrorResponse) {
    return throwError(response?.error?.errors);
  }
}
